// Shapes and fixed strings for the Pacvue "Update Listing For Vendor" bulk
// export. Split from pacvue-export-utils.ts the same way title-compliance and
// ai-optimize split constants from logic.
//
// SOURCE OF TRUTH: every literal in this file is transcribed byte-for-byte from
// the template Pacvue generates (`Update Listing For Vendor.xlsx`, supplied by
// Candice Morris 2026-09-16). Pacvue's uploader reads the file it handed out,
// so a "tidier" header string is a broken upload. Do not reflow, re-case, or
// ASCII-ify anything below without re-checking it against a freshly downloaded
// template.

/**
 * Pacvue's per-upload ceiling, stated in instruction 3 of the template itself:
 * "Due to API limitations, a maximum of 2,000 ASINs/P-ASINs can be updated each
 * time". Counts DATA rows only — the instruction and header rows don't count
 * against it, and each split file repeats them.
 *
 * Note this is the documented limit, not an observed-safe one: Candice hit
 * upload failures at ~1,700 ASINs (open Pacvue support ticket). Staying under
 * 2,000 is necessary but has not been shown to be sufficient.
 */
export const PACVUE_MAX_ROWS_PER_FILE = 2000;

/** Amazon marketplace for the `Market` column. Only US is in play today. */
export const PACVUE_DEFAULT_MARKET = 'US';

/**
 * Row 1 of the template, verbatim — a single cell in column A holding Pacvue's
 * instruction block, with the header row beneath it on row 2.
 *
 * We reproduce it because Pacvue's uploader consumes the same shape it exports,
 * so headers live on row 2 and dropping this row would shift them to row 1.
 * This is the one assumption in the exporter not yet proven against a live
 * upload — see the note on `PacvueExportConfig.includeInstructionsRow`.
 *
 * Contains commas, embedded newlines and full-width semicolons (U+FF1B); it
 * MUST go through CSV quoting rather than being concatenated raw.
 */
export const PACVUE_INSTRUCTIONS_ROW =
  'Instructions\n' +
  '1.Fields with a red background are required; Fields with a green background are listing content, fields that are not populated with values will not be updated;\n' +
  '2.There cannot be duplicate ASIN-Market data; The Account ID and Vendor Code set last time are automatically brought into the downloaded template; if they have not been set before, the user needs to fill them in；\n' +
  '3.Due to API limitations, a maximum of 2,000 ASINs/P-ASINs can be updated each time；\n' +
  '4.After the upload is completed, view the processing results through view log;\n' +
  '5.Short Product Description, Specific Uses for Product, and Product Benefits are fields only applicable to certain product categories. If they are not applicable to your products, these three columns can be left blank. For Specific Uses for Product (up to 20 entries, max 500 characters each) and Product Benefits (up to 6 entries, max 1,000 bytes each), enter multiple values within a single cell, separated by |. Example: Moisturizing|Anti-aging|Brightening.\n' +
  '6.Fill in the ASIN or P-ASIN column based on the update target; leave the other column blank.\n' +
  '7.When the update target is a Parent ASIN, at least one of UPC/EAN, ISBN, or Model Number is required; multiple fields may be provided. These fields are not required for Child ASIN updates and may be left blank.';

/**
 * Row 2 of the template, verbatim and in order. 26 columns, A–Z.
 *
 * `Keywords` carries FULL-WIDTH parentheses (U+FF08 / U+FF09), not ASCII ones.
 * That is what Pacvue emits; it is preserved deliberately.
 */
export const PACVUE_HEADERS = [
  'PDP Title',
  'ASIN',
  'P-ASIN',
  'Market',
  'Account ID',
  'Vendor Code',
  'EAN/UPC',
  'Model Number',
  'ISBN',
  'Keywords（Use a comma as the delimiter for keywords）',
  'Item Name',
  'Item Highlights',
  'Product Description',
  'Bullet Point 1',
  'Bullet Point 2',
  'Bullet Point 3',
  'Bullet Point 4',
  'Bullet Point 5',
  'Bullet Point 6',
  'Bullet Point 7',
  'Bullet Point 8',
  'Bullet Point 9',
  'Bullet Point 10',
  'Short Product Description',
  'Specific Uses for Product',
  'Product Benefits'
] as const;

/**
 * Named column indices into a `PACVUE_HEADERS`-shaped row, so writes read as
 * `row[PacvueColumn.ITEM_NAME] = …` rather than `row[10] = …`. Only the columns
 * we actually populate are named; the rest stay blank by construction.
 *
 * Column colour in the template encodes its contract:
 *   grey   (A)      reference only — the current title, for the human reviewer
 *   red    (B–F)    required
 *   yellow (G–I)    required only when the target is a P-ASIN
 *   green  (J–Z)    listing content — BLANK MEANS "DO NOT UPDATE THIS FIELD"
 *
 * That last rule is why the exporter leaves everything it did not generate
 * empty: a blank cell is a safe no-op, a guessed value is a live listing edit.
 */
export const PacvueColumn = {
  PDP_TITLE: 0,
  ASIN: 1,
  P_ASIN: 2,
  MARKET: 3,
  ACCOUNT_ID: 4,
  VENDOR_CODE: 5,
  ITEM_NAME: 10,
  ITEM_HIGHLIGHTS: 11
} as const;

/** Per-account identifiers Pacvue requires on every row (the red columns). */
export interface PacvueExportConfig {
  /** Pacvue account identifier. Retrieved from Pacvue per account (Bjorn owns this). */
  accountId: string;
  /** Amazon vendor code for the account. */
  vendorCode: string;
  /** Marketplace; defaults to `PACVUE_DEFAULT_MARKET`. */
  market?: string;
  /** Data rows per file. Defaults to `PACVUE_MAX_ROWS_PER_FILE`. */
  maxRowsPerFile?: number;
  /**
   * Emit Pacvue's instruction block as row 1, putting headers on row 2 exactly
   * as the downloaded template has them. Defaults to true.
   *
   * Exposed because it is the single un-validated assumption in this exporter:
   * nobody has yet round-tripped a generated file through a real Pacvue upload.
   * If Pacvue turns out to want headers on row 1, this flips to false and the
   * column order is unaffected.
   */
  includeInstructionsRow?: boolean;
}

/** Why a product was left out of the export. Surfaced to the user, never silent. */
export const PacvueSkipReason = {
  /** No AI rewrite has been generated or saved for this product yet. */
  NO_REWRITE: 'no-rewrite',
  /** A rewrite record exists but carries neither a title nor a highlight. */
  NOTHING_TO_UPDATE: 'nothing-to-update',
  /** The scraper never captured a usable ASIN, so Pacvue has nothing to match on. */
  MISSING_ASIN: 'missing-asin',
  /** An earlier row already claimed this ASIN+Market pair (template instruction 2). */
  DUPLICATE_ASIN: 'duplicate-asin',
  /** Stored title exceeds the 75-char cap — refuse to push a non-compliant title live. */
  TITLE_OVER_CAP: 'title-over-cap',
  /** Stored highlight exceeds the 125-char cap. */
  HIGHLIGHT_OVER_CAP: 'highlight-over-cap'
} as const;
export type PacvueSkipReasonType = (typeof PacvueSkipReason)[keyof typeof PacvueSkipReason];

/** Human-readable copy for each skip reason, for the export summary UI. */
export const PACVUE_SKIP_REASON_LABEL: Record<PacvueSkipReasonType, string> = {
  [PacvueSkipReason.NO_REWRITE]: 'No AI rewrite generated yet',
  [PacvueSkipReason.NOTHING_TO_UPDATE]: 'Rewrite has no title or highlight to send',
  [PacvueSkipReason.MISSING_ASIN]: 'No usable ASIN on the row',
  [PacvueSkipReason.DUPLICATE_ASIN]: 'Duplicate ASIN for this marketplace',
  [PacvueSkipReason.TITLE_OVER_CAP]: 'Saved title is over the 75-character cap',
  [PacvueSkipReason.HIGHLIGHT_OVER_CAP]: 'Saved item highlight is over the 125-character cap'
};

/** One product that did not make it into the export, with the reason why. */
export interface PacvueSkippedRow {
  productId: string;
  asin: string;
  reason: PacvueSkipReasonType;
}

/** A single ready-to-download CSV file. */
export interface PacvueExportFile {
  fileName: string;
  csv: string;
  /** Data rows only — excludes the instruction and header rows. */
  rowCount: number;
}

/** A file the export WILL produce, before its CSV text has been rendered. */
export interface PacvuePlannedFile {
  fileName: string;
  /** Data rows only — excludes the instruction and header rows. */
  rowCount: number;
  /** The selected data rows, ready to render. */
  rows: string[][];
}

/** The shape of a pending export: what would be written, without writing it. */
export interface PacvueExportPlan {
  files: PacvuePlannedFile[];
  skipped: PacvueSkippedRow[];
  exportedCount: number;
  errors: string[];
}

export interface PacvueExportResult {
  /** Empty when `errors` is non-empty, or when nothing was exportable. */
  files: PacvueExportFile[];
  /** Products deliberately left out, each with a reason. */
  skipped: PacvueSkippedRow[];
  /** Total data rows written across every file. */
  exportedCount: number;
  /** Config problems that blocked the export entirely (e.g. missing Account ID). */
  errors: string[];
}
